import { Component, Input, Output, EventEmitter, ViewChildren, QueryList } from '@angular/core';
import { UtilService, DialogService } from '../../../../shared/index';
import { EventRead } from '../../../shared/event-read.model';
import { RegistrationUpsert } from 'src/app/events-v4/shared/registration/crud/registration-upsert.model';
import { RegistrationItem } from 'src/app/events-v4/shared/registration/registration-item.model';
import { EventItemSummary } from '../../../shared/items/event-item-summary.model';
import { IValidationComponent } from '../../../../shared/widgets/component/ivalidation.component';
import { ValidationResult } from '../../../../shared/widgets/validators/validation-result.model';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { EventCustomRegField } from '../../../shared/custom-reg-fields/event-custom-reg-field.model';
import { RegistrationCustomRegFieldResponse } from 'src/app/events-v4/shared/registration/registration-custom-reg-field-response.model';
import { EventFieldVisibleRequiredEnum } from '../../../shared/event-field-visible-required.enum';
import { EventV4ItemService } from 'src/app/events-v4/shared/items/event-v4-item.service';
import { EventV4CustomRegFieldService } from 'src/app/events-v4/shared/custom-reg-fields/event-v4-custom-reg-field.service';
import { EventV4RegistrationService } from 'src/app/events-v4/shared/registration/event-v4-registration.service';
import { EventRegistrationActionTrackerService } from 'src/app/events-v4/shared/event-registration-action-tracker.service';
import { EventRegistationActionTypeEnum } from 'src/app/events-v4/shared/dto/event-registation-action-type.enum';
import { EventRegistrationEditActionDto } from 'src/app/events-v4/shared/dto/event-registration-edit-action.dto';

@Component({
  selector: 'wl-event-v4-registration-wizard-items',
  templateUrl: 'event-v4-registration-wizard-items.component.html',
  providers: [EventV4ItemService, EventV4CustomRegFieldService, EventV4RegistrationService],
    standalone: false
})
export class EventV4RegistrationWizardItemsComponent implements IValidationComponent {
  public registrationId: number;
  public get registration(): RegistrationUpsert {
    return this._registration;
  }
  public set registration(v: RegistrationUpsert) {
    this._registration = v;
    this.updateSelectedItemQuantityTracking();
  }

  public event: EventRead = new EventRead();
  public initialLoadComplete: boolean = false;

  @Output() onRegistrationItemsChanged: EventEmitter<Array<RegistrationItem>>;

  public _registration: RegistrationUpsert = new RegistrationUpsert();
  public _utilService: UtilService;
  public _eventItemService: EventV4ItemService;
  public _translationService: TranslationService;
  public _eventCustomRegFieldService: EventV4CustomRegFieldService;
  public _registrationService: EventV4RegistrationService;
  public _eventFieldVisibleRequiredEnum: any = EventFieldVisibleRequiredEnum;
  public _selectedEventItemIds: Array<number> = [];
  public _hasItemsWithMemberPricing: boolean = false;

  public availableItems: Array<EventItemSummary>;
  public itemTotal: number;
  public originalItems: Array<RegistrationItem>;
  //@ViewChildren('registrationWizardModificationsOptionComponent') registrationCustomFieldComponents: QueryList<RegistrationCustomFieldComponent>;

  public allPossibleCustomFields: Array<EventCustomRegField> = [];

  public constructor(
    utilService: UtilService,
    eventItemService: EventV4ItemService,
    translationService: TranslationService,
    eventCustomRegFieldService: EventV4CustomRegFieldService,
    registrationService: EventV4RegistrationService,
    private actionTrackerService: EventRegistrationActionTrackerService
  ) {
    this._utilService = utilService;
    this._eventItemService = eventItemService;
    this._translationService = translationService;
    this._eventCustomRegFieldService = eventCustomRegFieldService;
    this._registrationService = registrationService;

    this.availableItems = new Array<EventItemSummary>();
    this.onRegistrationItemsChanged = new EventEmitter<Array<RegistrationItem>>();

    this.itemTotal = 0;
    this.registrationId = 0;
    this.originalItems = new Array<RegistrationItem>();
  }

  public loadData = (): Promise<Array<RegistrationItem>> => {
    let allPromises: Array<Promise<any>> = [];
    let registrationCustomfields: Array<RegistrationCustomRegFieldResponse> = [];
    let registrationItems: Array<RegistrationItem> = [];

    allPromises.push(
      this._eventCustomRegFieldService.getEventCustomRegFields(this.event.EventId).then((data: any) => {
        this.allPossibleCustomFields = data.Result.sort((a, b) => {
          return a.DisplayOrder < b.DisplayOrder ? -1 : a.DisplayOrder > b.DisplayOrder ? 1 : 0;
        });
      })
    );

    if (this.event.EventId) {
      if (this.registration.IsEdit) {
        // load custom field responses for the current registration
        allPromises.push(
          this._registrationService
            .getRegistrationCustomFieldResponses(this.registrationId)
            .then((data: Array<RegistrationCustomRegFieldResponse>) => {
              registrationCustomfields = data;
            })
        );

        // load registration's items and set Display price.
        allPromises.push(
          this._registrationService.getRegistrationItemsMin(this.registrationId).then((data: Array<RegistrationItem>) => {
            registrationItems = data;
            registrationItems.forEach((element) => {
              element.DisplayPrice = element.ExtendedPrice * element.Quantity;
            });
          })
        );
      }
    }

    this._hasItemsWithMemberPricing = false;

    return Promise.all(allPromises).then(() => {
      this.initialLoadComplete = true;

      if (!this.event.EventId) return;

      this.registration.Items = new Array<RegistrationItem>();
      this.originalItems = new Array<RegistrationItem>();

      // check if there's member/non-member pricing
      this._hasItemsWithMemberPricing = this.event.Items.some((x) => x.HasMemberPricing);

      for (let availItem of this.event.Items) {
        let registrationItem = new RegistrationItem();
        this._utilService.map(availItem, registrationItem);
        registrationItem.Selected = false;

        //if this is edit add info for any saved event items on the reg
        for (let existRegItem of registrationItems) {
          if (existRegItem.EventItemId == availItem.EventItemId) {
            this._utilService.mapSafe(existRegItem, registrationItem);
          }
        }

        this.registration.Items.push(registrationItem);

        //set the original items
        let originalItem = new RegistrationItem();
        this._utilService.mapSafe(registrationItem, originalItem);
        this.originalItems.push(originalItem);
      }

      this.updateSelectedItemQuantityTracking();

      if (this.registration.IsEdit) {
        // process loaded custom fields
        let registrationResponses: Array<RegistrationCustomRegFieldResponse> = new Array<RegistrationCustomRegFieldResponse>();
        for (let response of registrationCustomfields.filter(function (x) {
          return !!x.CustomField;
        })) {
          if (
            (response.CustomField.Visible == EventFieldVisibleRequiredEnum.RegistrationOnly ||
              response.CustomField.Visible == EventFieldVisibleRequiredEnum.BothRegistrationAndAttendee) &&
            (response.AttendeeHeaderId == null || response.AttendeeHeaderId <= 0)
          ) {
            registrationResponses.push(response);
          }
        }

        this.registration.Registration.EventCustomRegFieldResponses = registrationResponses;
      } else {
        this.getItemTotals();
      }

      this.onRegistrationItemsChanged.emit(this.registration.Items);

      return this.registration.Items;
    });
  };

  public getValidationResult = (): ValidationResult => {
    let valid = new ValidationResult();
    valid.isValid = true;

    let filter: Array<RegistrationItem> = this.registration.Items.filter((item: RegistrationItem) => {
      return item.Quantity > 0;
    });

    if (filter.length <= 0) {
      valid.isValid = false;
      valid.errors.push(this._translationService.getInstant('events.registration.wizard.items.no-item-selected-validation-message'));
    }

    //this.registrationCustomFieldComponents.forEach((cfc) => {
    //    let vresult = cfc.getValidationResult();
    //    if (!vresult.isValid) {
    //        valid.isValid = false;
    //        valid.errors.concat(vresult.errors);
    //    }

    //});

    return valid;
  };

  public getMaxItemInput = (item: RegistrationItem): number => {
    if (item.MaxItemsAvail <= 0) {
      //set this high so we can bind the quantity input control max to it
      return 999999999;
    } else {
      return item.Remaining;
    }
  };

  public getItemTotals = (): void => {
    //emit the selected items
    let filter: Array<RegistrationItem> = this.registration.Items.filter((item: RegistrationItem) => {
      return item.Quantity > 0;
    });

    this.onRegistrationItemsChanged.emit(filter);

    // calculate after the above has updated
    setTimeout(() => {
      this.itemTotal = 0;
      let taxAmount: number = 0;
      this._registrationService.calculateTotals(this.registration);
      this.checkIfItemsHaveChanged();
      this.itemTotal = this.registration.Registration.PreTaxAmount;
    });
  };

  public quantityChanged = (): void => {
    this.updateSelectedItemQuantityTracking();
    setTimeout(() => {
      this.getItemTotals();
      this.actionTrackerService.addAction({
        Type: EventRegistationActionTypeEnum.EventItemQuantityChanged,
        OldValue: this.originalItems,
        NewValue: this.registration.Items,
      } as EventRegistrationEditActionDto);
    });
  };

  public hasCustomRegFields = (eventItemId: number): boolean => {
    return (
      this.registration.Registration.EventCustomRegFieldResponses &&
      this.registration.Registration.EventCustomRegFieldResponses.some(
        (x) =>
          (x.CustomField.Visible == EventFieldVisibleRequiredEnum.RegistrationOnly ||
            x.CustomField.Visible == EventFieldVisibleRequiredEnum.BothRegistrationAndAttendee) &&
          x.CustomField.EventItemIds &&
          x.CustomField.EventItemIds.some((ei) => ei == eventItemId)
      )
    );
  };

  public showCustomFields = (): boolean => {
    return this.registration.Registration.EventCustomRegFieldResponses.length > 0;
  };

  public onResponseChange = (response: RegistrationCustomRegFieldResponse): void => {
    this.getItemTotals();
  };

  public checkIfItemsHaveChanged = (): void => {
    this.registration.ItemsChanged = false;
    this.registration.EditNewItemsAdded = false;
    this.registration.EditOriginalItemsChanged = false;

    //if (this.originalItems.length != this.registration.Items.length) {
    //    this.registration.ItemsChanged = true
    //}

    if (this.registration.ItemsChanged == false) {
      //look at changes for individual items
      if (this.registration.IsEdit) {
        let regItemIndex = 0;
        for (let regItem of this.registration.Items) {
          if (
            this.originalItems[regItemIndex].EventItemId == regItem.EventItemId &&
            (this.originalItems[regItemIndex].Quantity != regItem.Quantity ||
              this.originalItems[regItemIndex].ItemAmountTotal != regItem.ItemAmountTotal)
          ) {
            this.registration.ItemsChanged = true;
          }

          if (regItem.Quantity > 0 && this.registration.EditNewItemsAdded == false) {
            //assume its a newly added item unless we find it below in the originals
            this.registration.EditNewItemsAdded = true;
            for (let origItem of this.originalItems) {
              //look to see if this is a completely new item that was not part of the original items
              if (origItem.Quantity > 0) {
                if (regItem.EventItemId == origItem.EventItemId) {
                  //it was found in the originals so not new
                  this.registration.EditNewItemsAdded = false;
                }
              }
            }
          }

          for (let origItem of this.originalItems) {
            if (origItem.Quantity > 0) {
              //this was selected in the original items
              if (regItem.EventItemId == origItem.EventItemId) {
                if (origItem.Quantity != regItem.Quantity || origItem.ItemAmountTotal != regItem.ItemAmountTotal) {
                  this.registration.EditOriginalItemsChanged = true;
                }
              }
            }
          }

          regItemIndex = regItemIndex + 1;
        }
      }
    }
  };

  public updateSelectedItemQuantityTracking(): void {
    let items = this.registration && this.registration.Items ? this.registration.Items : [];
    this._selectedEventItemIds = items.filter((x) => x.Quantity > 0).map((x) => x.EventItemId);
  }

  /**
   * triggered when the user clicks the "force member pricing" checkbox
   */
  public handleForceMemberPricingChange() {
    this._registrationService.calculateTotals(this.registration);
  }
}
